// Main-thread client for the AI search Web Worker. Keeps the heavy,
// synchronous SearchAI off the UI thread so a long think stays responsive.
//
// - Lazily spawns the worker (dist/search-worker.js, sibling of the main
//   bundle) on first use.
// - Caches the catalog in the worker, re-sending only when it grows (custom
//   cards merged mid-session), so it isn't re-serialized every search.
// - Forwards SearchProgress to an optional onProgress sink (drives the
//   "Thinking…" bar).
// - Falls back to a main-thread search (with a frame budget, so it at least
//   yields between root actions) if the worker can't be created or errors —
//   the search is deterministic and idempotent, so re-running is safe.

import type { CardDef, GameAction, GameState, PlayerSide } from '../types.js';
import type { AIProfile, SearchReport } from './types.js';
import { getSearchActionWithReport, type SearchProgress } from './SearchAI.js';
import type { WorkerRequest, WorkerResponse } from './search-worker-protocol.js';

export interface SearchResult { action: GameAction | null; report: SearchReport | null; }

const FALLBACK_FRAME_BUDGET_MS = 12;

let worker: Worker | null = null;
let workerBroken = false;
let lastCatalogSize = -1;
let nextReqId = 1;

interface Pending {
  resolve: (r: SearchResult) => void;
  reject: (e: unknown) => void;
  onProgress?: (p: SearchProgress) => void;
}
const pending = new Map<number, Pending>();

function failAllPending(reason: unknown): void {
  for (const [, p] of pending) p.reject(reason);
  pending.clear();
}

function initWorker(): Worker | null {
  if (worker || workerBroken) return worker;
  try {
    worker = new Worker(new URL('./search-worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;
      const entry = pending.get(msg.reqId);
      if (!entry) return;
      if (msg.type === 'progress') { entry.onProgress?.(msg.progress); return; }
      pending.delete(msg.reqId);
      if (msg.type === 'result') entry.resolve({ action: msg.action, report: msg.report });
      else entry.reject(new Error(msg.message));
    };
    worker.onerror = (e) => {
      // Worker-level failure (e.g. failed to load). Tear down and fall back;
      // pending searches reject and the caller re-runs on the main thread.
      workerBroken = true;
      const dead = worker;
      worker = null;
      lastCatalogSize = -1;
      failAllPending(new Error(`AI search worker error: ${e.message ?? 'unknown'}`));
      try { dead?.terminate(); } catch { /* ignore */ }
    };
    lastCatalogSize = -1;
    return worker;
  } catch {
    workerBroken = true;
    worker = null;
    return null;
  }
}

async function runOnWorker(
  w: Worker,
  state: GameState,
  catalog: Map<string, CardDef>,
  side: PlayerSide,
  profile: AIProfile,
  onProgress?: (p: SearchProgress) => void,
): Promise<SearchResult> {
  if (catalog.size !== lastCatalogSize) {
    const req: WorkerRequest = { type: 'setCatalog', cards: [...catalog.values()] };
    w.postMessage(req);
    lastCatalogSize = catalog.size;
  }
  const reqId = nextReqId++;
  return new Promise<SearchResult>((resolve, reject) => {
    pending.set(reqId, { resolve, reject, onProgress });
    const req: WorkerRequest = { type: 'search', reqId, state, side, profile };
    w.postMessage(req);
  });
}

/** Run one AI search, off the main thread when possible. */
export async function searchActionInWorker(
  state: GameState,
  catalog: Map<string, CardDef>,
  side: PlayerSide,
  profile: AIProfile,
  onProgress?: (p: SearchProgress) => void,
): Promise<SearchResult> {
  const w = initWorker();
  if (w) {
    try {
      return await runOnWorker(w, state, catalog, side, profile, onProgress);
    } catch (e) {
      console.warn('AI search worker unavailable, falling back to main thread:', e);
      workerBroken = true;
      try { worker?.terminate(); } catch { /* ignore */ }
      worker = null;
    }
  }
  return getSearchActionWithReport(state, catalog, side, profile, {
    frameBudgetMs: FALLBACK_FRAME_BUDGET_MS,
    onProgress,
  });
}
